import React, { Suspense, lazy, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// Eager: the public entry routes (fast first paint for landing/auth/SEO pages).
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ReturnsPolicy from "@/pages/ReturnsPolicy";
import AppShell from "@/components/AppShell";
import PublicShopShell from "@/components/PublicShopShell";
import PublicShell from "@/components/PublicShell";
import DownloadTeaser from "@/components/DownloadTeaser";

// Lazy: everything heavier — keeps recharts (dashboard/bloodwork), SheetJS
// (admin) and the mentor suite out of the initial bundle (PageSpeed: JS weight).
const Biomarkers = lazy(() => import("@/pages/Biomarkers"));
const BiomarkerDetail = lazy(() => import("@/pages/BiomarkerDetail"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Shop = lazy(() => import("@/pages/Shop"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const Orders = lazy(() => import("@/pages/Orders"));
const Bloodwork = lazy(() => import("@/pages/Bloodwork"));
const Mentor = lazy(() => import("@/pages/Mentor"));
const Settings = lazy(() => import("@/pages/Settings"));
const ReviewSubmit = lazy(() => import("@/pages/ReviewSubmit"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const Videos = lazy(() => import("@/pages/Videos"));
const Sol = lazy(() => import("@/pages/Sol"));

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center bg-surface">
      <div className="h-8 w-8 rounded-full border-2 border-line border-t-brand animate-spin" />
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

// Emergent Google-auth return point: the OAuth redirect lands on
// /app/dashboard#session_id=... — exchange it for a Better Days session.
// One-time exchange (useRef guard), navigates immediately after.
function AuthCallback() {
  const { loginWithGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) { navigate("/login", { replace: true }); return; }
    loginWithGoogleSession(sessionId).then((res) => {
      if (res.ok) navigate("/app/dashboard", { replace: true });
      else { toast.error(res.error || "Google sign-in failed — try email instead"); navigate("/login", { replace: true }); }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <Loader />;
}

// Checks the URL fragment during RENDER (not in an effect) so a fresh
// session_id is always processed before any route/auth logic runs. Reading
// useLocation().hash (reactive) is required — window.location.hash goes stale
// once the hash is cleared.
function SessionIdGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

export default function App() {
  // Base robots directive: keep non-production hosts (e.g. preview) out of
  // search indexes to avoid duplicate content. Per-page useSeo refines this.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hostname === "app.betterdays.fit") return;
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex,nofollow");
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
        <SessionIdGate>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
          <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
          <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
          <Route path="/sol" element={<Sol />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/returns-policy" element={<ReturnsPolicy />} />

          {/* Public shop — browsable without an account */}
          <Route path="/shop" element={<PublicShopShell />}>
            <Route index element={<Shop />} />
            <Route path=":slug" element={<ProductDetail />} />
          </Route>

          {/* Guest checkout — no account needed; one is created silently */}
          <Route path="/checkout" element={<PublicShopShell />}>
            <Route index element={<Checkout />} />
          </Route>

          {/* Public review capture — single-use tokenised link, no login */}
          <Route path="/review" element={<PublicShopShell />}>
            <Route path=":token" element={<ReviewSubmit />} />
          </Route>

          {/* Public videos — YouTube showcase; admins add/delete in place */}
          <Route path="/videos" element={<PublicShopShell />}>
            <Route index element={<Videos />} />
          </Route>

          {/* Public biomarker library — no account needed */}
          <Route path="/biomarkers" element={<PublicShell />}>
            <Route index element={<Biomarkers />} />
            <Route path=":slug" element={<BiomarkerDetail />} />
          </Route>

          <Route path="/app" element={<Protected><AppShell /></Protected>}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="shop" element={<Shop />} />
            <Route path="shop/:slug" element={<ProductDetail />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="orders" element={<Orders />} />
            <Route path="bloodwork" element={<Bloodwork />} />
            <Route path="mentor" element={<Mentor />} />
            <Route path="settings" element={<Settings />} />
            <Route path="admin" element={<AdminPanel />} />
            <Route path="admin/reviews" element={<AdminReviews />} />
            <Route path="clara" element={<DownloadTeaser feature="clara" />} />
            <Route path="meals" element={<DownloadTeaser feature="meals" />} />
            <Route path="workouts" element={<DownloadTeaser feature="workouts" />} />
            <Route path="social" element={<DownloadTeaser feature="social" />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </SessionIdGate>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}
